import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { getAllCommunitiesMinimal } from 'apis/CommunityAPI';

import {
  EMAIL_MATCHING_COMMUNICATION_TYPE_LABELS,
  EMAIL_MATCHING_DELIVERY_METHOD_LABELS,
  EMAIL_MATCHING_SENDER_TYPE_LABELS,
  EMAIL_MATCHING_STATUS_FILTERS,
} from './constants';
import EmailMatchingClassificationMatchingFields from './EmailMatchingClassificationMatchingFields';
import { EmailMatchingConfirmMatchPayloadType, EmailMatchingRecordType } from './types';

type Props = {
  detail: EmailMatchingRecordType;
  onValuesChange?: (values: EmailMatchingConfirmMatchPayloadType) => void;
};

type CommunityOptionType = {
  value: number;
  label: string;
};

const normalizeEmailTypes = (value?: number[] | null) => {
  if (!value || value.length === 0) {
    return [] as number[];
  }

  return value.filter((item) => Boolean(EMAIL_MATCHING_COMMUNICATION_TYPE_LABELS[item]));
};

const areArraysEqual = (first: number[], second: number[]) => {
  if (first.length !== second.length) {
    return false;
  }

  return first.every((value, index) => value === second[index]);
};

const getPreferredEnumValue = (fieldValue?: number | null, aiValue?: number | null) => {
  if (fieldValue !== null && fieldValue !== undefined) {
    return Number(fieldValue);
  }

  if (aiValue !== null && aiValue !== undefined) {
    return Number(aiValue);
  }

  return undefined;
};

const EmailMatchingClassificationMatchingFieldsContainer = ({ detail, onValuesChange }: Props) => {
  const disabled =
    detail.status === EMAIL_MATCHING_STATUS_FILTERS.MATCHED || detail.status === EMAIL_MATCHING_STATUS_FILTERS.SPAM;

  const [selectedEmailTypeValues, setSelectedEmailTypeValues] = useState<number[]>([]);
  const [selectedSenderType, setSelectedSenderType] = useState<number | undefined>(undefined);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<number | undefined>(undefined);
  const [selectedCommunityId, setSelectedCommunityId] = useState<number | undefined>(undefined);

  const { data: communitiesData, isLoading: isLoadingCommunities } = useQuery({
    queryKey: ['email-matching-communities-options'],
    queryFn: () => getAllCommunitiesMinimal(),
    refetchOnWindowFocus: false,
  });

  const communityOptions = useMemo<CommunityOptionType[]>(
    () =>
      (communitiesData ?? [])
        .map((community: any) => ({
          value: Number(community.id),
          label: community.comunity_name || community.community_name || community.name || '',
        }))
        .filter((option: CommunityOptionType) => Number.isFinite(option.value) && Boolean(option.label)),
    [communitiesData]
  );

  const emailTypeDefaultValues = useMemo(() => {
    const preferredEmailTypes =
      detail.communication_types !== null && detail.communication_types !== undefined
        ? detail.communication_types
        : detail.ai_communication_types;

    return normalizeEmailTypes(preferredEmailTypes);
  }, [detail.ai_communication_types, detail.communication_types]);

  const primaryCommunicationTypes = useMemo(
    () =>
      normalizeEmailTypes(detail.communication_types)
        .slice()
        .sort((a, b) => a - b),
    [detail.communication_types]
  );

  const aiCommunicationTypes = useMemo(
    () =>
      normalizeEmailTypes(detail.ai_communication_types)
        .slice()
        .sort((a, b) => a - b),
    [detail.ai_communication_types]
  );

  const preferredCommunityId = useMemo(() => {
    const isValidCommunityId = (id?: number) =>
      id !== undefined && communityOptions.some((option) => option.value === id);

    const matchedCommunityId = getPreferredEnumValue(detail.matched_community, undefined);
    const aiMatchedCommunityId = getPreferredEnumValue(undefined, detail.ai_matched_community);

    if (isValidCommunityId(matchedCommunityId)) {
      return matchedCommunityId;
    }

    if (isValidCommunityId(aiMatchedCommunityId)) {
      return aiMatchedCommunityId;
    }

    if (!detail.community_name) {
      return undefined;
    }

    const matchedByName = communityOptions.find(
      (option) => option.label?.trim()?.toLowerCase() === detail.community_name?.trim()?.toLowerCase()
    );

    return matchedByName?.value;
  }, [communityOptions, detail.ai_matched_community, detail.community_name, detail.matched_community]);

  const primaryMatchedCommunityId =
    detail.matched_community !== null && detail.matched_community !== undefined
      ? Number(detail.matched_community)
      : undefined;
  const aiMatchedCommunityId =
    detail.ai_matched_community !== null && detail.ai_matched_community !== undefined
      ? Number(detail.ai_matched_community)
      : undefined;
  const shouldShowCommunityWarning =
    aiMatchedCommunityId !== undefined &&
    Number.isFinite(aiMatchedCommunityId) &&
    aiMatchedCommunityId !== primaryMatchedCommunityId;

  const aiSuggestedCommunityLabel = useMemo(() => {
    if (aiMatchedCommunityId === undefined) {
      return null;
    }

    const communityOption = communityOptions.find((option) => option.value === aiMatchedCommunityId);
    return communityOption?.label || `Community #${aiMatchedCommunityId}`;
  }, [aiMatchedCommunityId, communityOptions]);

  const primarySenderType =
    detail.sender_type !== null && detail.sender_type !== undefined ? Number(detail.sender_type) : undefined;
  const aiSenderType =
    detail.ai_sender_type !== null && detail.ai_sender_type !== undefined ? Number(detail.ai_sender_type) : undefined;
  const shouldShowSenderTypeWarning =
    aiSenderType !== undefined && Number.isFinite(aiSenderType) && aiSenderType !== primarySenderType;

  const primaryDeliveryMethod =
    detail.delivery_method !== null && detail.delivery_method !== undefined
      ? Number(detail.delivery_method)
      : undefined;
  const aiDeliveryMethod =
    detail.ai_delivery_method !== null && detail.ai_delivery_method !== undefined
      ? Number(detail.ai_delivery_method)
      : undefined;
  const shouldShowDeliveryMethodWarning =
    aiDeliveryMethod !== undefined && Number.isFinite(aiDeliveryMethod) && aiDeliveryMethod !== primaryDeliveryMethod;

  const shouldShowCommunicationTypesWarning =
    aiCommunicationTypes.length > 0 && !areArraysEqual(primaryCommunicationTypes, aiCommunicationTypes);
  const aiSuggestedCommunicationTypesText = aiCommunicationTypes
    .map((value) => EMAIL_MATCHING_COMMUNICATION_TYPE_LABELS[value])
    .filter(Boolean)
    .join(', ');

  useEffect(() => {
    setSelectedEmailTypeValues(emailTypeDefaultValues);
  }, [emailTypeDefaultValues]);

  useEffect(() => {
    const senderType = getPreferredEnumValue(detail.sender_type, detail.ai_sender_type);
    setSelectedSenderType(
      senderType !== undefined && EMAIL_MATCHING_SENDER_TYPE_LABELS[senderType] ? senderType : undefined
    );
  }, [detail.ai_sender_type, detail.sender_type]);

  useEffect(() => {
    const deliveryMethod = getPreferredEnumValue(detail.delivery_method, detail.ai_delivery_method);
    setSelectedDeliveryMethod(
      deliveryMethod !== undefined && EMAIL_MATCHING_DELIVERY_METHOD_LABELS[deliveryMethod] ? deliveryMethod : undefined
    );
  }, [detail.ai_delivery_method, detail.delivery_method]);

  useEffect(() => {
    setSelectedCommunityId(preferredCommunityId);
  }, [preferredCommunityId]);

  useEffect(() => {
    if (!onValuesChange) {
      return;
    }

    onValuesChange({
      matched_community: selectedCommunityId ?? null,
      sender_type: selectedSenderType ?? null,
      delivery_method: selectedDeliveryMethod ?? null,
      communication_types: selectedEmailTypeValues,
    });
  }, [onValuesChange, selectedCommunityId, selectedDeliveryMethod, selectedEmailTypeValues, selectedSenderType]);

  const handleEmailTypesSelect = (values: Array<number | string>) => {
    const nextValues = (values ?? []).map((value) => Number(value)).filter((value) => Number.isFinite(value));
    setSelectedEmailTypeValues(nextValues);
  };

  const handleDeliveryMethodChange = (value: number | string | null | undefined) =>
    setSelectedDeliveryMethod(value === null || value === undefined || value === '' ? undefined : Number(value));
  const handleSenderTypeChange = (value: number | string | null | undefined) =>
    setSelectedSenderType(value === null || value === undefined || value === '' ? undefined : Number(value));

  const handleCommunityChange = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') {
      setSelectedCommunityId(undefined);
      return;
    }

    setSelectedCommunityId(Number(value));
  };

  return (
    <EmailMatchingClassificationMatchingFields
      selectedCommunityId={selectedCommunityId}
      communityOptions={communityOptions}
      isLoadingCommunities={isLoadingCommunities}
      onCommunityChange={handleCommunityChange}
      shouldShowCommunityWarning={shouldShowCommunityWarning}
      aiSuggestedCommunityLabel={aiSuggestedCommunityLabel}
      selectedSenderType={selectedSenderType}
      onSenderTypeChange={handleSenderTypeChange}
      shouldShowSenderTypeWarning={shouldShowSenderTypeWarning}
      aiSenderType={aiSenderType}
      selectedDeliveryMethod={selectedDeliveryMethod}
      onDeliveryMethodChange={handleDeliveryMethodChange}
      shouldShowDeliveryMethodWarning={shouldShowDeliveryMethodWarning}
      aiDeliveryMethod={aiDeliveryMethod}
      selectedEmailTypeValues={selectedEmailTypeValues}
      onEmailTypesSelect={handleEmailTypesSelect}
      shouldShowCommunicationTypesWarning={shouldShowCommunicationTypesWarning}
      aiSuggestedCommunicationTypesText={aiSuggestedCommunicationTypesText}
      disabled={disabled}
    />
  );
};

export default EmailMatchingClassificationMatchingFieldsContainer;
